import React, { useEffect, useState } from 'react';
import axios from 'axios';

// Define the API Base URL (assuming you can access it via a global/context or pass it as a prop)
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// --- RiskDetailModal Component ---
// Modified to accept roleReference and API token as props
const RiskDetailModal = ({ isOpen, onClose, data, token }) => {
    // State to hold the detailed data fetched from the API
    const [detailedData, setDetailedData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    // Use the primary data reference if available
    const roleReference = data?.reference;
    
    // The data object used for display (either initial prop data or fetched detail)
    // We default to the prop data for name/reference, and use detailedData for metrics
    const displayData = detailedData || data;

    // useEffect to fetch data when the modal opens and a role reference is available
    useEffect(() => {
        if (!isOpen || !roleReference || !token) {
            setDetailedData(null); // Clear data when closed
            return;
        }

        const fetchDetails = async () => {
            setIsLoading(true);
            try {
                // The API call to get the specific role's detail data
                const response = await axios.get(
                    `${API_BASE_URL}/roles/get_risk_role_details/${roleReference}/`,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );
                // Assuming the detailed data containing matchingMatric is in response.data
                setDetailedData(response.data); 
            } catch (error) {
                console.error("Error fetching risk role details:", error);
                // Optionally handle error state
                setDetailedData(null);
            } finally {
                setIsLoading(false);
            }
        };

        fetchDetails();
    }, [isOpen, roleReference, token]); // Dependencies: Refetch when modal opens for a different role

    if (!isOpen || !displayData) return null;

    // --- Utility Functions (Clamping, Metrics Definition, Data Access, Risk Logic) ---

    const clampPercent = (value) => {
        const v = parseFloat(value);
        if (isNaN(v)) return 0;
        return Math.max(0, Math.min(v, 100));
    };

    // This data structure is from your RoleAtRisk columns and calculations
    const riskMetrics = [
        { 
            label: "Longlist to Briefed Conversion %", 
            key: "briefed_dropped_vs_profiles_added_pct", 
            threshold: 30, 
            riskBelow: true, 
            isPercent: true 
        },
        { 
            label: "Brief to Evaluate Conversion %", 
            key: "brief_to_eval_conversion_pct", 
            threshold: 30, 
            riskBelow: true, 
            isPercent: true 
        },
        { 
            label: "Recommend to Shortlist %", 
            key: "recommend_to_shortlist_pct", 
            threshold: 60, 
            riskBelow: true, 
            isPercent: true 
        },
        { 
            label: "Profiles Dropped %", 
            key: "drop_percentage_of_profiles_added", 
            threshold: 60, 
            riskBelow: false, 
            isPercent: true 
        },
        { 
            label: "30%+ Shortlisted Pending Interviews %", 
            key: "interview_setup_to_complete_pct", 
            threshold: 70, 
            riskBelow: true, 
            isPercent: true 
        },
        { 
            label: "Briefing stuck 1 week", 
            key: "last_brief_within_1_weeks", 
            expectedValue: "No", 
            isBoolean: true 
        },
        { 
            label: "No Recent Recommendation (Since 10 days)", 
            key: "evaluated_no_recent_recommendation", 
            expectedValue: "No", 
            isBoolean: true 
        },
        
        { 
            label: "Offer Acceptance Delay (> 10 days)", 
            key: "interview_done_no_offer_closed_10_days", 
            expectedValue: "No", 
            isBoolean: true 
        },
    ];

    const getMetricValue = (key) => {
    // 1. Try to get the value from the root of the displayData (used by initial row data)
    let value = displayData?.[key];

    // 2. If not found, try the nested matchingMatric structure (used by API detail call)
    if (value === undefined || value === null || value === 'N/A') {
        value = displayData.matchingMatric?.[0]?.[key];
    }
    
    // Fallback to 'N/A'
    return value ?? 'N/A';
};
    

   const isRiskConditionMet = (metric) => {
    const rawValue = getMetricValue(metric.key);

    if (metric.isBoolean) {
        return (rawValue || "").toUpperCase() !== (metric.expectedValue || "").toUpperCase();
    }

    // ✅ normalize exactly like display
    const numValue = clampPercent(rawValue);

    return metric.riskBelow
        ? numValue < metric.threshold
        : numValue > metric.threshold;
};

    
    // --- Render Logic ---

    return (
        <div 
            className="modal" 
            style={{ display: isOpen ? 'block' : 'none', backgroundColor: 'rgba(0,0,0,0.5)', position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 1050, overflow: 'auto' }}
        >
            <div className="modal-dialog modal-lg" style={{ margin: '1.75rem auto' }}>
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Risk Details for: {displayData.name} ({displayData.reference})</h5>
                        <button type="button" className="btn-close" onClick={onClose}></button>
                    </div>
                    <div className="modal-body">
                        {isLoading ? (
                            <p>Loading role details...</p>
                        ) : (
                            <>
                                <h4 className="mb-3">
                                    Overall Risk Score: 
                                    <span style={{ color: displayData.weighted_points >= 50 ? 'red' : 'black', fontWeight: 'bold', marginLeft: '10px' }}>
                                        {/* Ensure weighted_points is present, otherwise show N/A or 0.0% */}
                                        {displayData.weighted_points ? `${displayData.weighted_points.toFixed(1)}%` : 'N/A'}
                                    </span>
                                </h4>
                                <hr />
                                <h6>Individual Risk Metrics:</h6>
                                <ul className="list-group list-group-flush">
                                    {riskMetrics.map((metric) => {
                                        const value = getMetricValue(metric.key);
                                        const isAtRisk = isRiskConditionMet(metric);
                                        // The original display logic is fine for percentages and boolean values
                                        const displayValue = metric.isPercent ? `${clampPercent(value)}%` : value;

                                        return (
                                            <li 
                                                key={metric.key} 
                                                className="list-group-item d-flex justify-content-between align-items-center"
                                                style={{ backgroundColor: isAtRisk ? '#fce8e8' : 'white' }} 
                                            >
                                                <span>{metric.label}</span>
                                                <span style={{ color: isAtRisk ? 'red' : 'black', fontWeight: 'bold' }}>
                                                    {displayValue}
                                                </span>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </>
                        )}
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};


export default RiskDetailModal;